import { useMemo, useState } from 'react';
import { APP_VERSION } from '@technohull/shared';
import { useAudit, type AuditAbout, type AuditEntry } from '../../features/admin.js';
import { auditActionName as actionName, auditResourceName } from '../../features/auditNames.js';
import { releaseTitleOf, useCodingStats } from '../../features/codingStats.js';
import { useNameResolver } from '../../features/directory.js';
import { ErrorState, FilterBar, FilterSelect, LoadingState, PageHeader, Pill, SearchInput, SyncLabel } from '../../components/ui.js';
import { useDateFormatter } from '../../lib/format.js';
import { useIsPhone } from '../../lib/media.js';
import { fold, hasText } from '../../lib/search.js';
import { AdminBack } from './AdminHub.js';

// what the app itself does (a version going live, a vessel flagged by the new-vessel watch) is logged with the
// actor 'system'
const isSystem = (actor: string | null) => (actor ?? '').trim().toLowerCase() === 'system';
const isRelease = (e: Pick<AuditEntry, 'action'>) => e.action === 'release.published';

/** an entry about a sheet row, in words: "Service · BORA › Antifouling", then "Status → Completed" */
function aboutWords(about: AuditAbout, deleted: boolean) {
  const where = about.path.join(' › ');
  const change = about.columns.join(', ');
  const value = about.value === undefined ? undefined : about.value || '(cleared)';
  // a deletion's row is gone by definition; say so only on other entries
  const gone = about.gone && !deleted;
  return { where, change, value, gone, text: [about.sheet, where, change, value, gone ? 'row since deleted' : ''].filter(Boolean).join(' ') };
}

function AboutCell({ about, deleted }: { about: AuditAbout; deleted: boolean }) {
  const w = aboutWords(about, deleted);
  return (
    <>
      <span className="a-where"><span className="a-sheet">{about.sheet}</span>{w.where && <> · <span className="a-boat">{w.where}</span></>}</span>
      {(w.change || w.value !== undefined || w.gone) && (
        <span className="a-sum">
          {w.change}
          {w.value !== undefined && <>{w.change ? ' → ' : ''}<span className="a-val">{w.value}</span></>}
          {w.gone && <span className="a-gone">{w.change || w.value !== undefined ? ' · ' : ''}row since deleted</span>}
        </span>
      )}
    </>
  );
}

const isDeletion = (action: string) => /delete_row$/.test(action);

/** the periods the log can show, in hours; it opens on the past day */
const PERIODS = [
  { hours: 24, label: 'Past 24 hours' },
  { hours: 24 * 7, label: 'Past 7 days' },
  { hours: 24 * 30, label: 'Past 30 days' },
] as const;
const DEFAULT_HOURS = 24;

export function AdminAudit() {
  // every entry of the period (the past day unless another is picked). The log shows at once; a second call
  // names each sheet row's boat, work and column (it reads those sheets) and replaces it when it lands
  const [hours, setHours] = useState<number>(DEFAULT_HOURS);
  const period = PERIODS.find((p) => p.hours === hours) ?? PERIODS[0];
  const light = useAudit(hours);
  const described = useAudit(hours, { describe: true });
  const data = described.data ?? light.data;
  const isLoading = !data && light.isLoading;
  const isFetching = light.isFetching || described.isFetching;
  const error = data ? null : light.error;
  const refetch = () => Promise.all([light.refetch(), described.refetch()]);
  // each version's one-line summary, for its "New version launched" row
  const { data: releases } = useCodingStats();
  const resolveName = useNameResolver();
  const nameOf = (actor: string | null) => (isSystem(actor) ? 'System' : resolveName(actor));
  const summaryOf = (e: AuditEntry) => (isRelease(e) ? releaseTitleOf(releases, e.resource) : '');
  const resourceText = (e: AuditEntry) =>
    e.about ? aboutWords(e.about, isDeletion(e.action)).text : auditResourceName(e.resource, e.detail, resolveName) ?? '';
  const df = useDateFormatter();
  const isPhone = useIsPhone();
  const [q, setQ] = useState('');
  const [actionF, setActionF] = useState('');
  const activeFilters = (q.trim() ? 1 : 0) + (actionF ? 1 : 0) + (hours !== DEFAULT_HOURS ? 1 : 0);
  const clearFilters = () => { setQ(''); setActionF(''); setHours(DEFAULT_HOURS); };

  const entries = data?.entries ?? [];
  const actions = useMemo(() => [...new Set(entries.map((e) => e.action))].sort((a, b) => actionName(a).localeCompare(actionName(b))), [entries]);
  const rows = entries.filter((e) => {
    if (actionF && e.action !== actionF) return false;
    const query = fold(q.trim());
    if (query) {
      const hay = [e.actor, nameOf(e.actor), e.action, actionName(e.action), e.resource, resourceText(e), summaryOf(e), e.ip, e.status].join(' ');
      if (!hasText(query, hay)) return false;
    }
    return true;
  });
  // a period busier than the server sends at once shows its latest entries and says so
  const more = data?.more ?? false;
  const total = `${entries.length}${more ? '+' : ''}`;
  const count = rows.length === entries.length && !more ? `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}` : `${rows.length} of ${total}`;

  return (
    <>
      <AdminBack />
      <PageHeader title="Audit Log">
        <span className="pill adm-ver" title="The version currently running" style={{ marginRight: 4 }}>v{APP_VERSION}</span>
        {/* phones: the running version rides on the count line under the title instead of a header pill */}
        {data && <span className="muted page-count">{count}<span className="adm-ver-inline"> · running v{APP_VERSION}</span></span>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} idle="Refresh" busyText="Refreshing…" /></button>
      </PageHeader>
      <FilterBar
        search={<SearchInput value={q} onChange={setQ} placeholder={isPhone ? 'Name, action, boat, IP' : 'Search name / action / boat / IP'} width={240} />}
        activeCount={activeFilters}
        onClear={clearFilters}
      >
        <FilterSelect
          label="Period"
          ariaLabel="Period"
          value={String(hours)}
          onChange={(v) => setHours(Number(v) || DEFAULT_HOURS)}
          options={PERIODS.map((p) => ({ value: String(p.hours), label: p.label }))}
        />
        <FilterSelect
          label="Action"
          ariaLabel="Filter by action"
          value={actionF}
          onChange={setActionF}
          options={[{ value: '', label: 'All actions' }, ...actions.map((a) => ({ value: a, label: actionName(a) }))]}
        />
      </FilterBar>
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && (
        <>
          {/* the cards drop the column headings, so the one that carries meaning moves up here */}
          {more && (
            <p className="muted" style={{ margin: '0 0 8px' }}>
              The {period.label.toLowerCase()} held more than {entries.length.toLocaleString()} entries: these are the latest. Pick a shorter period to see all of them.
            </p>
          )}
          <p className="muted adm-caption">Times in your local time</p>
          <div className="tbl-wrap">
            {/* phones: a card per event: the action with its status pill (DENIED shows at a glance), then
                actor + time and resource + IP; failed rows are tinted */}
            <table className="tbl tbl-cards adm-audit">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>IP</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const release = isRelease(e);
                  const summary = summaryOf(e);
                  const named = !release && !e.about ? auditResourceName(e.resource, e.detail, resolveName) : null;
                  return (
                    <tr key={e.id} className={`a-row${e.status && e.status !== 'ok' ? ' denied' : ''}${release ? ' is-release' : ''}`}>
                      <td style={{ whiteSpace: 'nowrap' }} className="muted a-time">{df.dateTime(e.ts, { seconds: true })}</td>
                      <td className="a-actor" title={e.actor ?? ''}>{isSystem(e.actor) ? <span className="a-sys">System</span> : e.actor ? nameOf(e.actor) : '-'}</td>
                      <td className="a-action" title={e.action}>{actionName(e.action)}</td>
                      <td className="muted a-res" title={e.resource ?? undefined}>
                        {release && e.resource ? <><span className="a-ver">{e.resource}</span>{summary && <span className="a-sum">{summary}</span>}</>
                          : e.about ? <AboutCell about={e.about} deleted={isDeletion(e.action)} />
                          : named ?? e.resource ?? '-'}
                      </td>
                      <td className="muted a-ip">{e.ip ?? '-'}</td>
                      <td className="a-status">{e.status ? <Pill kind={e.status === 'ok' ? 'ok' : 'bad'}>{e.status}</Pill> : '-'}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>{entries.length ? 'No entries match those filters.' : `Nothing was logged in the ${period.label.toLowerCase()}.`}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
